import makeRequest from '../../utils/api';

/**
 * kAI v2 consumer REST client (contract §1). The streaming `/ask` endpoint is
 * handled separately in `sse.js`; everything here is plain JSON over the shared
 * axios instance (which attaches the bearer token + token-refresh interceptor).
 */
const BASE = '/v1/kai';

/**
 * Pull a human message out of a kai error, whichever transport raised it.
 * Non-SSE errors are klikit house style — `{ message }` or `{ validation_error }`
 * — carried on `err.body` for fetch/SSE failures (sse.js) and on
 * `err.response.data` for axios failures (makeRequest). The legacy
 * `{ error: { message } }` envelope is still accepted defensively.
 */
export const errorMessageOf = (err, fallback = 'Something went wrong.') => {
  const body = err?.body || err?.response?.data || {};
  return (
    body.message || body.validation_error || body.error?.message || fallback
  );
};

export const createConversation = ({ businessId, title }) =>
  makeRequest({
    url: `${BASE}/conversations`,
    method: 'post',
    data: { business_id: businessId, ...(title ? { title } : {}) },
  });

export const listConversations = ({ businessId, pageNo = 1, pageSize = 20 }) =>
  makeRequest({
    url: `${BASE}/conversations`,
    method: 'get',
    params: { business_id: businessId, page_no: pageNo, page_size: pageSize },
  });

export const getConversationMessages = ({
  id,
  businessId,
  pageNo = 1,
  pageSize = 50,
}) =>
  makeRequest({
    url: `${BASE}/conversations/${id}/messages`,
    method: 'get',
    params: {
      business_id: businessId,
      page_no: pageNo,
      page_size: pageSize,
    },
  });

export const deleteConversation = ({ id, businessId }) =>
  makeRequest({
    url: `${BASE}/conversations/${id}`,
    method: 'delete',
    params: { business_id: businessId },
  });

export const getUsage = businessId =>
  makeRequest({
    url: `${BASE}/usage`,
    method: 'get',
    params: { business_id: businessId },
  });

/**
 * All branches / brands for a business — used to populate /ask context so
 * analyseit tools get branch_id/brand_id (required upstream). Same endpoints
 * Native Analytics uses (`filterByBusiness`).
 */
export const listBranches = businessId =>
  makeRequest({
    url: 'v1/branch',
    method: 'get',
    params: { filterByBusiness: businessId, page: 1, size: 1000 },
  });

export const listBrands = businessId =>
  makeRequest({
    url: 'v1/brand',
    method: 'get',
    params: { filterByBusiness: businessId, page: 1, size: 1000 },
  });
